import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, X, Instagram, Heart, MapPin, Users2, ArrowUpDown } from "lucide-react";
import { theme } from "@/theme";

interface Profile {
  id: string;
  name: string;
  image?: string;
  city?: string;
  country?: string;
  countryCode?: string;
  partner?: string;
  instagram?: string;
  type?: "individual" | "couple";
  edition?: string;
  createdAt?: string;
}

type TabType = "dancers" | "couples";
type SortType = "newest" | "a-z";

const PROFILES_PER_PAGE = 16;

// Country code to name mapping
const COUNTRY_NAMES: Record<string, string> = {
  US: "United States", CA: "Canada", GB: "United Kingdom", AU: "Australia",
  NZ: "New Zealand", IE: "Ireland", DE: "Germany", FR: "France", ES: "Spain",
  IT: "Italy", PT: "Portugal", NL: "Netherlands", BE: "Belgium", CH: "Switzerland",
  AT: "Austria", SE: "Sweden", NO: "Norway", DK: "Denmark", FI: "Finland",
  PL: "Poland", CZ: "Czech Republic", HU: "Hungary", RO: "Romania", BG: "Bulgaria",
  GR: "Greece", TR: "Turkey", RU: "Russia", UA: "Ukraine", JP: "Japan",
  CN: "China", KR: "South Korea", IN: "India", TH: "Thailand", VN: "Vietnam",
  SG: "Singapore", MY: "Malaysia", PH: "Philippines", ID: "Indonesia", BR: "Brazil",
  MX: "Mexico", AR: "Argentina", CL: "Chile", CO: "Colombia", PE: "Peru",
  ZA: "South Africa", EG: "Egypt", IL: "Israel", AE: "United Arab Emirates",
  SA: "Saudi Arabia", NG: "Nigeria", KE: "Kenya", MA: "Morocco"
};

const getCountryName = (code: string): string => {
  return COUNTRY_NAMES[code] || code;
};

/**
 * Sanitize Instagram username to prevent XSS attacks
 * Only allows valid Instagram username characters: alphanumeric, dots, and underscores
 */
const sanitizeInstagramUsername = (username: string): string => {
  if (!username) return '';
  
  // Remove @ symbol if present
  let cleaned = username.replace(/^@/, '');
  
  // Only allow valid Instagram username characters (alphanumeric, dots, underscores)
  // Instagram usernames can only contain letters, numbers, periods, and underscores
  cleaned = cleaned.replace(/[^a-zA-Z0-9._]/g, '');
  
  // Limit length to Instagram's max username length (30 characters)
  cleaned = cleaned.slice(0, 30);
  
  return cleaned;
};

const ProfileDirectory = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>("dancers");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedCity, setSelectedCity] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [sortBy, setSortBy] = useState<SortType>("newest");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [displayCount, setDisplayCount] = useState(PROFILES_PER_PAGE);

  // Fetch profiles from community data
  useEffect(() => {
    fetch("https://cbuxrdarzwtolyjhnhzw.supabase.co/storage/v1/object/public/globe-cache/community_map.json")
      .then(res => res.json())
      .then(data => {
        const users = Array.isArray(data) ? data : data.data;
        
        // Transform to profile format
        const profileData = users.map((user: any, index: number) => ({
          id: user.id || `profile-${index}`,
          name: user.name || "Anonymous",
          image: user.avatar || user.photoUrl || user.profile_image || user.image,
          city: user.city,
          country: getCountryName(user.country), // Decode country code to full name
          countryCode: user.country,
          partner: user.partner_name,
          instagram: user.instagram_handle || user.instagram,
          type: user.accountType || user.type || (user.partner_name ? "couple" : "individual") as "individual" | "couple",
          edition: user.edition || user.cohort,
          createdAt: user.created_at || user.joined_at || new Date().toISOString(),
        }));
        
        setProfiles(profileData);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
      });
  }, []);

  // Get unique countries for filters
  const countries = useMemo(() => {
    const unique = new Set(profiles.map(p => p.country).filter(Boolean));
    return Array.from(unique).sort();
  }, [profiles]);

  // Get unique cities for filters (filtered by country if selected)
  const cities = useMemo(() => {
    const filteredByCountry = selectedCountry === "all" 
      ? profiles 
      : profiles.filter(p => p.country === selectedCountry);
    const unique = new Set(filteredByCountry.map(p => p.city).filter(Boolean));
    return Array.from(unique).sort();
  }, [profiles, selectedCountry]);

  // Filter profiles by tab and filters
  const filteredProfiles = useMemo(() => {
    let filtered = profiles.filter(profile => {
      // Tab filter
      const matchesTab = activeTab === "dancers" 
        ? profile.type === "individual" 
        : profile.type === "couple";
      
      // Other filters
      const matchesSearch = profile.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCountry = selectedCountry === "all" || profile.country === selectedCountry;
      const matchesCity = selectedCity === "all" || profile.city === selectedCity;
      const matchesType = selectedType === "all" || profile.type === selectedType;
      
      return matchesTab && matchesSearch && matchesCountry && matchesCity && matchesType;
    });

    // Apply sorting
    if (sortBy === "a-z") {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      // Sort by newest (createdAt)
      filtered.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });
    }

    return filtered;
  }, [profiles, activeTab, searchQuery, selectedCountry, selectedCity, selectedType, sortBy]);

  // Get profiles to display (with pagination)
  const displayedProfiles = useMemo(() => {
    return filteredProfiles.slice(0, displayCount);
  }, [filteredProfiles, displayCount]);

  // Check if there are more profiles to load
  const hasMore = displayCount < filteredProfiles.length;

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCountry("all");
    setSelectedCity("all");
    setSelectedType("all");
    setSortBy("newest");
    setDisplayCount(PROFILES_PER_PAGE);
  };

  // Load more profiles
  const loadMore = () => {
    setDisplayCount(prev => prev + PROFILES_PER_PAGE);
  };

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(PROFILES_PER_PAGE);
  }, [searchQuery, selectedCountry, selectedCity, selectedType, sortBy, activeTab]);

  // Get country flag emoji
  const getCountryFlag = (countryCode?: string) => {
    if (!countryCode || countryCode.length !== 2) return "🌍";
    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 rounded-full"
          style={{
            borderColor: `${theme.colors.accent}40`,
            borderTopColor: theme.colors.accent,
          }}
        />
      </div>
    );
  }

  return (
    <section className="relative py-20 px-4 bg-gradient-to-b from-black via-[#0a0a0f] to-black">
      <div className="relative max-w-7xl mx-auto">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            style={{
              fontFamily: "'Bensoon', sans-serif",
              color: theme.colors.text,
              letterSpacing: "-0.02em",
            }}
          >
            Community <span style={{ color: theme.colors.accent }}>Directory</span>
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto"
            style={{
              fontFamily: "'Bensoon', sans-serif",
              color: theme.colors.secondary,
            }}
          >
            Meet our talented dancers and couples from around the world
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div 
            className="inline-flex rounded-xl p-1"
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              border: `1px solid ${theme.colors.border}`,
            }}
          >
            <button
              onClick={() => setActiveTab("dancers")}
              className="relative px-8 py-3 rounded-lg transition-all duration-300"
              style={{
                fontFamily: "'Bensoon', sans-serif",
                fontWeight: 600,
                color: activeTab === "dancers" ? theme.colors.text : theme.colors.textMuted,
                background: activeTab === "dancers" 
                  ? `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`
                  : "transparent",
              }}
            >
              Dancers
            </button>
            <button
              onClick={() => setActiveTab("couples")}
              className="relative px-8 py-3 rounded-lg transition-all duration-300"
              style={{
                fontFamily: "'Bensoon', sans-serif",
                fontWeight: 600,
                color: activeTab === "couples" ? theme.colors.text : theme.colors.textMuted,
                background: activeTab === "couples" 
                  ? `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`
                  : "transparent",
              }}
            >
              Couples
            </button>
          </div>
        </motion.div>

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none"
                size={20}
                style={{ color: theme.colors.textMuted }}
              />
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl outline-none transition-all duration-300"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: `1px solid ${theme.colors.border}`,
                  color: theme.colors.text,
                  fontFamily: "'Bensoon', sans-serif",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.accent;
                  e.currentTarget.style.boxShadow = `0 0 20px ${theme.colors.accent}30`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Country Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowCountryDropdown(!showCountryDropdown);
                  setShowCityDropdown(false);
                  setShowTypeDropdown(false);
                  setShowSortDropdown(false);
                }}
                className="w-full md:w-48 px-4 py-3 rounded-xl flex items-center justify-between transition-all duration-300"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: `1px solid ${theme.colors.border}`,
                  color: theme.colors.text,
                  fontFamily: "'Bensoon', sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                }}
              >
                <span>{selectedCountry === "all" ? "All Countries" : selectedCountry}</span>
                <ChevronDown size={20} />
              </button>

              <AnimatePresence>
                {showCountryDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full mt-2 w-full max-h-64 overflow-y-auto rounded-xl z-50"
                    style={{
                      background: "rgba(10, 10, 10, 0.98)",
                      border: `1px solid ${theme.colors.accent}40`,
                      boxShadow: `0 10px 40px ${theme.colors.accent}20`,
                      backdropFilter: "blur(20px)",
                    }}
                  >
                    <button
                      onClick={() => {
                        setSelectedCountry("all");
                        setSelectedCity("all");
                        setShowCountryDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left transition-colors duration-200"
                      style={{
                        color: theme.colors.text,
                        fontFamily: "'Bensoon', sans-serif",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${theme.colors.accent}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      All Countries
                    </button>
                    {countries.map((country) => (
                      <button
                        key={country}
                        onClick={() => {
                          setSelectedCountry(country);
                          setSelectedCity("all");
                          setShowCountryDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left transition-colors duration-200"
                        style={{
                          color: theme.colors.text,
                          fontFamily: "'Bensoon', sans-serif",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${theme.colors.accent}20`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        {country}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* City Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowCityDropdown(!showCityDropdown);
                  setShowCountryDropdown(false);
                  setShowTypeDropdown(false);
                  setShowSortDropdown(false);
                }}
                className="w-full md:w-48 px-4 py-3 rounded-xl flex items-center justify-between transition-all duration-300"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: `1px solid ${theme.colors.border}`,
                  color: theme.colors.text,
                  fontFamily: "'Bensoon', sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                }}
              >
                <span>{selectedCity === "all" ? "All Cities" : selectedCity}</span>
                <ChevronDown size={20} />
              </button>

              <AnimatePresence>
                {showCityDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full mt-2 w-full max-h-64 overflow-y-auto rounded-xl z-50"
                    style={{
                      background: "rgba(10, 10, 10, 0.98)",
                      border: `1px solid ${theme.colors.accent}40`,
                      boxShadow: `0 10px 40px ${theme.colors.accent}20`,
                      backdropFilter: "blur(20px)",
                    }}
                  >
                    <button
                      onClick={() => {
                        setSelectedCity("all");
                        setShowCityDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left transition-colors duration-200"
                      style={{
                        color: theme.colors.text,
                        fontFamily: "'Bensoon', sans-serif",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${theme.colors.accent}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      All Cities
                    </button>
                    {cities.length > 0 ? cities.map((city) => (
                      <button
                        key={city}
                        onClick={() => {
                          setSelectedCity(city);
                          setShowCityDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left transition-colors duration-200"
                        style={{
                          color: theme.colors.text,
                          fontFamily: "'Bensoon', sans-serif",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${theme.colors.accent}20`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        {city}
                      </button>
                    )) : (
                      <div className="px-4 py-2 text-sm" style={{ color: theme.colors.textMuted }}>
                        No cities available
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Type Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowTypeDropdown(!showTypeDropdown);
                  setShowCountryDropdown(false);
                  setShowCityDropdown(false);
                  setShowSortDropdown(false);
                }}
                className="w-full md:w-48 px-4 py-3 rounded-xl flex items-center justify-between transition-all duration-300"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: `1px solid ${theme.colors.border}`,
                  color: theme.colors.text,
                  fontFamily: "'Bensoon', sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                }}
              >
                <span>
                  {selectedType === "all" 
                    ? "All Types" 
                    : selectedType === "individual" 
                    ? "Individual" 
                    : "Couple"}
                </span>
                <ChevronDown size={20} />
              </button>

              <AnimatePresence>
                {showTypeDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full mt-2 w-full rounded-xl z-50"
                    style={{
                      background: "rgba(10, 10, 10, 0.98)",
                      border: `1px solid ${theme.colors.accent}40`,
                      boxShadow: `0 10px 40px ${theme.colors.accent}20`,
                      backdropFilter: "blur(20px)",
                    }}
                  >
                    <button
                      onClick={() => {
                        setSelectedType("all");
                        setShowTypeDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left transition-colors duration-200"
                      style={{
                        color: theme.colors.text,
                        fontFamily: "'Bensoon', sans-serif",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${theme.colors.accent}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      All Types
                    </button>
                    <button
                      onClick={() => {
                        setSelectedType("individual");
                        setShowTypeDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left transition-colors duration-200"
                      style={{
                        color: theme.colors.text,
                        fontFamily: "'Bensoon', sans-serif",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${theme.colors.accent}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      Individual
                    </button>
                    <button
                      onClick={() => {
                        setSelectedType("couple");
                        setShowTypeDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left transition-colors duration-200"
                      style={{
                        color: theme.colors.text,
                        fontFamily: "'Bensoon', sans-serif",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${theme.colors.accent}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      Couple
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSortDropdown(!showSortDropdown);
                  setShowCountryDropdown(false);
                  setShowCityDropdown(false);
                  setShowTypeDropdown(false);
                }}
                className="w-full md:w-48 px-4 py-3 rounded-xl flex items-center justify-between transition-all duration-300"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: `1px solid ${theme.colors.border}`,
                  color: theme.colors.text,
                  fontFamily: "'Bensoon', sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                }}
              >
                <ArrowUpDown size={18} style={{ marginRight: "8px" }} />
                <span>{sortBy === "newest" ? "Newest" : "A-Z"}</span>
                <ChevronDown size={20} />
              </button>

              <AnimatePresence>
                {showSortDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full mt-2 w-full rounded-xl z-50"
                    style={{
                      background: "rgba(10, 10, 10, 0.98)",
                      border: `1px solid ${theme.colors.accent}40`,
                      boxShadow: `0 10px 40px ${theme.colors.accent}20`,
                      backdropFilter: "blur(20px)",
                    }}
                  >
                    <button
                      onClick={() => {
                        setSortBy("newest");
                        setShowSortDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left transition-colors duration-200"
                      style={{
                        color: theme.colors.text,
                        fontFamily: "'Bensoon', sans-serif",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${theme.colors.accent}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      Newest
                    </button>
                    <button
                      onClick={() => {
                        setSortBy("a-z");
                        setShowSortDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left transition-colors duration-200"
                      style={{
                        color: theme.colors.text,
                        fontFamily: "'Bensoon', sans-serif",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${theme.colors.accent}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      A-Z
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Clear Button */}
            {(searchQuery || selectedCountry !== "all" || selectedCity !== "all" || selectedType !== "all" || sortBy !== "newest") && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={clearFilters}
                className="px-4 py-3 rounded-xl flex items-center gap-2 transition-all duration-300"
                style={{
                  background: `${theme.colors.accent}20`,
                  border: `1px solid ${theme.colors.accent}40`,
                  color: theme.colors.accent,
                  fontFamily: "'Bensoon', sans-serif",
                  fontWeight: 600,
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X size={18} />
                Clear
              </motion.button>
            )}
          </div>

          {/* Results Count */}
          <p
            style={{
              fontFamily: "'Bensoon', sans-serif",
              color: theme.colors.textMuted,
              fontSize: "14px",
            }}
          >
            Showing {displayedProfiles.length} of {filteredProfiles.length} {activeTab === "dancers" ? "dancers" : "couples"}
            {filteredProfiles.length !== profiles.length && ` (${profiles.length} total)`}
          </p>
        </motion.div>

        {/* Profile Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          <AnimatePresence mode="popLayout">
            {displayedProfiles.map((profile, index) => (
              <motion.div
                key={profile.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="group relative rounded-2xl overflow-hidden"
                style={{
                  background: "rgba(0, 0, 0, 0.6)",
                  border: `1px solid ${theme.colors.border}`,
                  backdropFilter: "blur(10px)",
                }}
              >
                {/* Hover Glow Effect */}
                <div
                  className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"
                  style={{
                    background: `linear-gradient(135deg, ${theme.colors.accent}40, ${theme.colors.accentLight}40)`,
                  }}
                />

                {/* Card Content Wrapper */}
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="relative"
                >
                  {/* Profile Image Section */}
                  <div className="relative aspect-[3/4] overflow-hidden">
                    {profile.image ? (
                      <motion.img
                        src={profile.image}
                        alt={profile.name}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.3 }}
                        onError={(e) => {
                          // Fallback to placeholder on error
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling?.classList.remove("hidden");
                        }}
                      />
                    ) : null}
                    
                    {/* Placeholder for missing images */}
                    <div
                      className={profile.image ? "hidden" : ""}
                      style={{
                        width: "100%",
                        height: "100%",
                        background: `linear-gradient(135deg, ${theme.colors.accent}30, ${theme.colors.accentLight}30)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <Users2 size={64} style={{ color: theme.colors.accent, opacity: 0.8 }} />
                    </div>

                    {/* Gradient Overlay on Hover */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      style={{
                        background: `linear-gradient(to top, rgba(230, 126, 34, 0.2), transparent)`,
                      }}
                    />

                    {/* Name Overlay */}
                    <div
                      className="absolute bottom-0 left-0 right-0 p-4"
                      style={{
                        background: "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.8) 50%, transparent)",
                      }}
                    >
                      <h3
                        className="font-bold text-lg leading-tight"
                        style={{
                          fontFamily: "'Bensoon', sans-serif",
                          color: theme.colors.text,
                          textShadow: "0 2px 8px rgba(0,0,0,0.8)",
                        }}
                      >
                        {profile.name}
                      </h3>
                    </div>
                  </div>

                  {/* Profile Details */}
                  <div className="p-5 space-y-3">
                    {/* Location */}
                    {(profile.city || profile.country) && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors duration-200"
                        style={{
                          background: "rgba(255, 255, 255, 0.03)",
                          border: `1px solid ${theme.colors.border}`,
                        }}
                      >
                        <span className="text-xl">{getCountryFlag(profile.countryCode)}</span>
                        <span
                          style={{
                            fontFamily: "'Bensoon', sans-serif",
                            color: theme.colors.secondary,
                            fontSize: "13px",
                            fontWeight: 500,
                          }}
                        >
                          {profile.city ? `${profile.city}, ` : ""}{profile.country}
                        </span>
                      </motion.div>
                    )}

                    {/* Partner */}
                    {profile.partner && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.15 }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors duration-200"
                        style={{
                          background: `${theme.colors.accent}10`,
                          border: `1px solid ${theme.colors.accent}30`,
                        }}
                      >
                        <Heart size={14} style={{ color: theme.colors.accent }} />
                        <span
                          style={{
                            fontFamily: "'Bensoon', sans-serif",
                            color: theme.colors.secondary,
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          {profile.partner}
                        </span>
                      </motion.div>
                    )}

                    {/* Instagram */}
                    {profile.instagram && (
                      <motion.a
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                        href={`https://instagram.com/${encodeURIComponent(sanitizeInstagramUsername(profile.instagram))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-300 group/button"
                        style={{
                          background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
                          color: theme.colors.text,
                          fontFamily: "'Bensoon', sans-serif",
                          fontSize: "14px",
                          fontWeight: 600,
                          boxShadow: `0 4px 12px ${theme.colors.accent}30`,
                        }}
                        whileHover={{ 
                          scale: 1.03,
                          boxShadow: `0 6px 20px ${theme.colors.accent}50`,
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Instagram size={18} />
                        <span>{profile.instagram}</span>
                      </motion.a>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Load More Button */}
        {hasMore && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center mt-12"
          >
            <motion.button
              onClick={loadMore}
              className="px-8 py-4 rounded-xl font-semibold transition-all duration-300"
              style={{
                background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
                color: theme.colors.text,
                fontFamily: "'Bensoon', sans-serif",
                fontSize: "16px",
                boxShadow: `0 4px 20px ${theme.colors.accent}30`,
              }}
              whileHover={{ 
                scale: 1.05,
                boxShadow: `0 6px 30px ${theme.colors.accent}50`,
              }}
              whileTap={{ scale: 0.95 }}
            >
              Load More ({filteredProfiles.length - displayCount} remaining)
            </motion.button>
          </motion.div>
        )}

        {/* No Results Message */}
        {filteredProfiles.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <p
              className="text-xl mb-4"
              style={{
                fontFamily: "'Bensoon', sans-serif",
                color: theme.colors.secondary,
              }}
            >
              No {activeTab === "dancers" ? "dancers" : "couples"} found matching your filters
            </p>
            <button
              onClick={clearFilters}
              className="px-6 py-3 rounded-xl transition-all duration-300"
              style={{
                background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
                color: theme.colors.text,
                fontFamily: "'Bensoon', sans-serif",
                fontWeight: 600,
              }}
            >
              Clear Filters
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ProfileDirectory;

